import { useLocation } from "@tanstack/react-router";
import * as React from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { sidebarModules } from "@/lib/sidebar-config";

export interface SidebarV2ContextProps {
	activeModule: string | null;
	setActiveModule: (id: string | null) => void;
	isPanelOpen: boolean;
	setIsPanelOpen: (open: boolean) => void;
	searchQuery: string;
	setSearchQuery: (query: string) => void;
	hoveredModule: string | null;
	setHoveredModule: (id: string | null) => void;
	hoverPosition: { top: number } | null;
	setHoverPosition: (pos: { top: number } | null) => void;
	hoverLeaveTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
	clearHoverLeaveTimeout: () => void;
	startHoverLeaveTimeout: () => void;
	suppressTooltips: boolean;
	triggerTooltipSuppression: () => void;
	isMobileMenuOpen: boolean;
	setIsMobileMenuOpen: (open: boolean) => void;
	/** Arkusz nawigacji na telefonie: "more" („Więcej”), id modulu albo null. */
	mobileNavSheet: string | null;
	setMobileNavSheet: (sheet: string | null) => void;
}

const SidebarV2Context = React.createContext<SidebarV2ContextProps | null>(null);

export function useSidebarV2() {
	const context = React.useContext(SidebarV2Context);
	if (!context) {
		throw new Error("useSidebarV2 must be used within SidebarV2Provider");
	}
	return context;
}

export function SidebarV2Provider({ children }: { children: React.ReactNode }) {
	const pathname = useLocation({ select: (l) => l.pathname });
	const [activeModule, setActiveModule] = React.useState<string | null>(null);
	const [isPanelOpen, setIsPanelOpen] = React.useState(true);
	const [searchQuery, setSearchQuery] = React.useState("");
	const [hoveredModule, setHoveredModule] = React.useState<string | null>(null);
	const [hoverPosition, setHoverPosition] = React.useState<{
		top: number;
	} | null>(null);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
	const [mobileNavSheet, setMobileNavSheet] = React.useState<string | null>(null);

	React.useEffect(() => {
		setIsMobileMenuOpen(false);
	}, []);

	const hoverLeaveTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const tooltipSuppressionTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const [suppressTooltips, setSuppressTooltips] = React.useState(false);

	const clearHoverLeaveTimeout = React.useCallback(() => {
		if (hoverLeaveTimeoutRef.current) {
			clearTimeout(hoverLeaveTimeoutRef.current);
			hoverLeaveTimeoutRef.current = null;
		}
	}, []);

	const startHoverLeaveTimeout = React.useCallback(() => {
		clearHoverLeaveTimeout();
		hoverLeaveTimeoutRef.current = setTimeout(() => {
			setHoveredModule(null);
			setHoverPosition(null);
		}, 200);
	}, [clearHoverLeaveTimeout]);

	const triggerTooltipSuppression = React.useCallback(() => {
		setSuppressTooltips(true);
		if (tooltipSuppressionTimeoutRef.current) {
			clearTimeout(tooltipSuppressionTimeoutRef.current);
		}
		tooltipSuppressionTimeoutRef.current = setTimeout(() => {
			setSuppressTooltips(false);
		}, 500);
	}, []);

	React.useEffect(() => {
		setMobileNavSheet(null);
		const module = sidebarModules.find((m) => {
			if (m.items) {
				return m.items.some((item) => pathname.startsWith(item.href));
			}
			if (m.groups) {
				return m.groups.some((g) => g.items.some((item) => pathname.startsWith(item.href)));
			}
			return false;
		});
		if (module) {
			setActiveModule(module.id);
		}
	}, [pathname]);

	return (
		<SidebarV2Context.Provider
			value={{
				activeModule,
				setActiveModule,
				isPanelOpen,
				setIsPanelOpen,
				searchQuery,
				setSearchQuery,
				hoveredModule,
				setHoveredModule,
				hoverPosition,
				setHoverPosition,
				hoverLeaveTimeoutRef,
				clearHoverLeaveTimeout,
				startHoverLeaveTimeout,
				suppressTooltips,
				triggerTooltipSuppression,
				isMobileMenuOpen,
				setIsMobileMenuOpen,
				mobileNavSheet,
				setMobileNavSheet,
			}}
		>
			<TooltipProvider delayDuration={0}>{children}</TooltipProvider>
		</SidebarV2Context.Provider>
	);
}
